// ============================================================
// OperatorNotificationsPage.js
// Route /platform/operator-notifications — the Slice D (2026-07-11)
// toggle surface for internal-to-A2Z-IT operator alerts
// (PlatformOperatorNotifications): per-type on/off Switch + a
// channel dropdown (Email + Teams / Email only / Teams only).
//
// Type keys are code-owned (seeded by migration 20260711-02);
// there is deliberately NO add form — a new type is a backend
// code change + seed row (mirrors PlatformNotificationTypesPage's
// no-create posture). Recipients are the admin UPN allowlist
// (ADMIN_UPN_ALLOWLIST) — not configurable here by design.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Switch from '../components/Switch';
import { toast } from '../components/Toaster';
import { listOperatorNotifications, patchOperatorNotification } from '../api/adminPlatform';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import './PlatformPages.css';

const TYPE_LABELS = {
  trial_submitted:      'New trial request submitted',
  trial_expiry_warning: 'Trial expiring soon (7-day warning)',
  client_trial_expired: 'Client trial expired',
  client_purged:        'Client data purged',
  invitation_redeemed:  'Invitation redeemed',
};

const CHANNEL_OPTIONS = [
  { value: '',      label: 'Email + Teams' },
  { value: 'email', label: 'Email only' },
  { value: 'teams', label: 'Teams only' },
];

function labelFor(typeKey) {
  return TYPE_LABELS[typeKey] || typeKey;
}

export default function OperatorNotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingKey, setPendingKey] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listOperatorNotifications();
      setItems(res.items || []);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  async function applyPatch(typeKey, body) {
    setPendingKey(typeKey);
    try {
      await patchOperatorNotification(typeKey, body);
      await fetchData();
      toast.success('Operator notification updated');
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setPendingKey(null);
    }
  }

  const columns = [
    {
      key: 'type',
      label: 'Notification',
      render: (row) => (
        <div>
          <div>{labelFor(row.notificationTypeKey)}</div>
          <code className="platform-page__code">{row.notificationTypeKey}</code>
        </div>
      ),
    },
    {
      key: 'enabled',
      label: 'Enabled',
      width: '110px',
      render: (row) => (
        <Switch
          checked={row.isEnabled}
          disabled={pendingKey === row.notificationTypeKey}
          onChange={(checked) => applyPatch(row.notificationTypeKey, { isEnabled: checked })}
          ariaLabel={`${labelFor(row.notificationTypeKey)} enabled`}
        />
      ),
    },
    {
      key: 'channels',
      label: 'Channels',
      width: '170px',
      render: (row) => (
        <select
          value={row.channelOverride || ''}
          disabled={pendingKey === row.notificationTypeKey || !row.isEnabled}
          onChange={(e) => applyPatch(row.notificationTypeKey, { channelOverride: e.target.value || null })}
          aria-label={`${labelFor(row.notificationTypeKey)} channels`}
        >
          {CHANNEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ),
    },
    {
      key: 'updated',
      label: 'Last changed',
      width: '220px',
      render: (row) => (
        <span>
          {row.updatedAt ? formatDate(row.updatedAt) : '—'}
          {row.updatedBy ? ` · ${row.updatedBy}` : ''}
        </span>
      ),
    },
  ];

  return (
    <div className="platform-page">
      <PageHeader
        title="Operator Notifications"
        subtitle="Internal A2Z IT alerts (trial pipeline, lifecycle, redemptions). Recipients come from the admin UPN allowlist; toggle types off here as volume grows."
      />
      <DataTable
        data={items}
        columns={columns}
        rowKey={(row) => row.notificationTypeKey}
        loading={loading}
        error={error}
        onRetry={fetchData}
        totalCount={items.length}
        page={1}
        pageSize={items.length || 1}
        onPageChange={() => {}}
        emptyMessage="No operator notification types found — the 20260711-02 seed has not run."
      />
    </div>
  );
}
